import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { ShoppingCart, Trash2, X } from 'lucide-react';
import { PageHeader, Button, Modal, Input } from '@/components/shared/ui';
import { ClassItem } from '../../data/classes';
import { useAuth } from '../../contexts/AuthContext';
import { getLocalizedClassItem, classDetailDtoToClassItem } from '@/utils/classUtils';
import { ROUTES } from '../../lib/constants/routes';
import {
    parseCartStorage,
    persistCartItems,
    CART_UPDATED_EVENT,
    type CartItemEntry,
} from '@/utils/cartStorage';
import { classKeys } from '@/data/queries/useClasses';
import { getClassDetail } from '@/services/classService';
import { getApiLanguageCode } from '@/lib/i18nLocale';
import { formatCurrencyForLocale } from '@/lib/currencyFormat';

const QUOTE_REQUESTS_KEY = 'quoteRequests';

export type { CartItemEntry };

export interface QuoteRequestRecord {
    id: string;
    organizationName: string;
    name: string;
    department: string;
    phone: string;
    email: string;
    message: string;
    selectedClassIds: string[];
    selectedItems: CartItemEntry[];
    createdAt: string;
}

export default function Cart(): React.ReactElement {
    const { t, i18n } = useTranslation('order');
    const navigate = useNavigate();
    const { user, getCurrentRole } = useAuth();
    const role = getCurrentRole();

    const [cartItems, setCartItems] = useState<CartItemEntry[]>([]);
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const [quoteModalOpen, setQuoteModalOpen] = useState(false);
    const [quoteForm, setQuoteForm] = useState({
        organizationName: user?.currentAccount?.organizationName ?? '',
        name: user?.name ?? '',
        phone: '',
        email: user?.email ?? '',
        message: ''
    });

    const loadCart = useCallback(() => {
        const raw = localStorage.getItem('cart');
        const items = parseCartStorage(raw);
        setCartItems(items);
        setSelectedItems(new Set(items.map(i => i.id)));
    }, []);

    useEffect(() => {
        loadCart();
        const onUpdate = () => loadCart();
        window.addEventListener(CART_UPDATED_EVENT, onUpdate);
        return () => window.removeEventListener(CART_UPDATED_EVENT, onUpdate);
    }, [loadCart]);

    const apiLang = getApiLanguageCode(i18n.language);
    // TODO: 용도에 맞는 API인지 확인 필요
    // 문제: 장바구니 데이터에는 classId만 있고 클래스 상세 정보가 없음
    // → N개 장바구니 아이템마다 클래스 상세 API N개 호출 (N+1 문제)
    const detailQueries = useQueries({
        queries: cartItems.map((entry) => {
            const idNum = parseInt(entry.id, 10);
            const valid = Number.isFinite(idNum) && idNum > 0;
            return {
                queryKey: [...classKeys.detail(idNum), apiLang],
                queryFn: ({ signal }) =>
                    getClassDetail(idNum, {
                        signal,
                        language: apiLang,
                    }),
                enabled: cartItems.length > 0 && valid,
                staleTime: 60_000,
            };
        }),
    });

    const classes: Record<string, ClassItem> = useMemo(() => {
        const m: Record<string, ClassItem> = {};
        for (let i = 0; i < cartItems.length; i++) {
            const dto = detailQueries[i]?.data;
            if (dto) {
                m[cartItems[i].id] = classDetailDtoToClassItem(dto, {
                    organizationName: dto.partnerOrganizationTitle ?? dto.organizationTitle ?? undefined,
                });
            }
        }
        return m;
    }, [cartItems, detailQueries]);

    useEffect(() => {
        setQuoteForm(prev => ({
            ...prev,
            organizationName: user?.currentAccount?.organizationName ?? prev.organizationName,
            name: user?.name ?? prev.name,
            email: user?.email ?? prev.email
        }));
    }, [user?.currentAccount?.organizationName, user?.name, user?.email]);

    const removeFromCart = (classId: string) => {
        const updatedCart = cartItems.filter(e => e.id !== classId);
        setCartItems(updatedCart);
        persistCartItems(updatedCart);
        const newSelected = new Set(selectedItems);
        newSelected.delete(classId);
        setSelectedItems(newSelected);
    };

    const toggleSelectAll = () => {
        if (selectedItems.size === cartItems.length) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(new Set(cartItems.map(i => i.id)));
        }
    };

    const toggleSelect = (classId: string) => {
        const newSelected = new Set(selectedItems);
        if (newSelected.has(classId)) newSelected.delete(classId);
        else newSelected.add(classId);
        setSelectedItems(newSelected);
    };

    const removeSelected = () => {
        const ids = new Set(selectedItems);
        const updatedCart = cartItems.filter(e => !ids.has(e.id));
        setCartItems(updatedCart);
        persistCartItems(updatedCart);
        setSelectedItems(new Set());
    };

    const cartClasses = cartItems
        .map(entry => classes[entry.id])
        .filter(Boolean) as ClassItem[];

    const selectedEntries = cartItems.filter(e => selectedItems.has(e.id));

    const totalPrice = selectedEntries.reduce<number>((sum, entry) => {
        const item = classes[entry.id] as ClassItem | undefined;
        if (!item) return sum;
        const price = item.discountPrice !== undefined ? item.discountPrice : (item.price || 0);
        return sum + price * entry.quantity;
    }, 0);

    const totalSelectedCount = selectedEntries.reduce((acc, e) => acc + e.quantity, 0);

    const handleCheckout = () => {
        if (selectedItems.size === 0) {
            alert(t('alerts.selectClassForCheckout'));
            return;
        }
        if (role === 'guest') {
            navigate(ROUTES.AUTH.EMAIL_VERIFICATION);
            return;
        }
        const items = selectedEntries.map(e => ({ classId: e.id, quantity: e.quantity }));
        navigate(ROUTES.COMMON.ORDER_CONFIRM, {
            state: {
                items,
                selectedClassIds: items.map(i => i.classId),
                totalPrice
            }
        });
    };

    const handleQuoteRequest = () => {
        if (selectedItems.size === 0) {
            alert(t('alerts.selectClassForQuote'));
            return;
        }
        setQuoteModalOpen(true);
    };

    const submitQuoteRequest = () => {
        const { organizationName, name, phone, email, message } = quoteForm;
        if (!organizationName.trim() || !name.trim() || !phone.trim() || !email.trim()) {
            alert(t('alerts.quoteRequiredFields'));
            return;
        }
        const list: QuoteRequestRecord[] = JSON.parse(localStorage.getItem(QUOTE_REQUESTS_KEY) || '[]');
        const record: QuoteRequestRecord = {
            id: `QR-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            organizationName: organizationName.trim(),
            name: name.trim(),
            department: '',
            phone: phone.trim(),
            email: email.trim(),
            message: message.trim(),
            selectedClassIds: selectedEntries.map(e => e.id),
            selectedItems: selectedEntries,
            createdAt: new Date().toISOString()
        };
        list.unshift(record);
        localStorage.setItem(QUOTE_REQUESTS_KEY, JSON.stringify(list));
        setQuoteModalOpen(false);
        setQuoteForm({ organizationName: '', name: '', phone: '', email: '', message: '' });
        alert(t('alerts.quoteSuccess'));
    };

    return (
        <div className="max-w-[1280px] mx-auto px-4 py-8">
            <PageHeader
                title={t('cart.pageTitle')}
            />

            <div className="mt-8">
                {cartItems.length === 0 ? (
                    <div className="rounded-lg p-12 text-center">
                        <ShoppingCart size={64} className="mx-auto mb-4 text-gray-300" />
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                            장바구니가 비어있습니다
                        </h3>
                        <p className="text-gray-500 mb-6">
                            관심있는 클래스를 장바구니에 담아보세요
                        </p>
                        <Button
                            variant="primary"
                            onClick={() => navigate(ROUTES.COMMON.CLASS_LIST)}
                        >
                            클래스 둘러보기
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-lg border border-gray-400 shadow-sm">
                                <div className="flex items-center justify-between p-4">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedItems.size === cartItems.length && cartItems.length > 0}
                                            onChange={toggleSelectAll}
                                            className="w-4 h-4 text-brand-600 rounded focus:ring-brand-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('cart.selectAll')}</span>
                                    </div>
                                    <button
                                        onClick={removeSelected}
                                        disabled={selectedItems.size === 0}
                                        className="text-sm text-gray-800 rounded-md px-2 py-1 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
                                    >
                                        <Trash2 size={14} />
                                        {t('cart.removeSelected')}
                                    </button>
                                </div>

                                {cartItems.map((entry, idx) => {
                                    const idNum = parseInt(entry.id, 10);
                                    const validId = Number.isFinite(idNum) && idNum > 0;
                                    const q = detailQueries[idx];
                                    const item = classes[entry.id] as ClassItem | undefined;

                                    if (!validId) {
                                        return (
                                            <div key={entry.id} className="p-4 border-b border-gray-200 flex items-center justify-between gap-4">
                                                <p className="text-sm text-red-600 flex-1">{t('cart.invalidClassId', { id: entry.id })}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFromCart(entry.id)}
                                                    className="text-gray-400 p-2 rounded-md hover:text-gray-800"
                                                    aria-label={t('cart.delete')}
                                                >
                                                    <X size={20} />
                                                </button>
                                            </div>
                                        );
                                    }

                                    if (!item) {
                                        if (q?.isPending) {
                                            return (
                                                <div key={entry.id} className="p-4 border-b border-gray-200">
                                                    <p className="text-sm text-gray-500">{t('cart.itemLoading')}</p>
                                                </div>
                                            );
                                        }
                                        if (q?.isError) {
                                            return (
                                                <div key={entry.id} className="p-4 border-b border-gray-200 flex items-center justify-between gap-4">
                                                    <p className="text-sm text-red-600 flex-1">{t('cart.itemLoadError')}</p>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFromCart(entry.id)}
                                                        className="text-gray-400 p-2 rounded-md hover:text-gray-800"
                                                        aria-label={t('cart.delete')}
                                                    >
                                                        <X size={20} />
                                                    </button>
                                                </div>
                                            );
                                        }
                                        return null;
                                    }

                                    const displayItem = getLocalizedClassItem(item, i18n.language);
                                    const price = item.discountPrice !== undefined ? item.discountPrice : (item.price || 0);
                                    const lineTotal = price * entry.quantity;
                                    return (
                                        <div key={entry.id} className="p-4 hover:bg-gray-50 border-b border-gray-200 rounded-md">
                                            <div className="flex items-center gap-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.has(entry.id)}
                                                    onChange={() => toggleSelect(entry.id)}
                                                    className="w-4 h-4 text-blue-500 rounded focus:ring-blue-500 mt-1"
                                                />
                                                <div
                                                    className="w-32 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
                                                    onClick={() => navigate(`/open-class/${entry.id}`)}
                                                >
                                                    {item.thumbnail ? (
                                                        <img src={item.thumbnail} alt={displayItem.title} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3
                                                        className="text-base font-bold text-gray-900 mb-1 cursor-pointer hover:text-blue-600"
                                                        onClick={() => navigate(`/open-class/${entry.id}`)}
                                                    >
                                                        {displayItem.title}
                                                    </h3>
                                                    {displayItem.organizationName && (
                                                        <p className="text-sm font-medium text-gray-800 mb-2">{displayItem.organizationName}</p>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <span className="text-sm font-medium text-gray-900 w-20 text-right">
                                                        {formatCurrencyForLocale(lineTotal, i18n.language)}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => removeFromCart(entry.id)}
                                                    className="text-gray-400 p-2 rounded-md hover:text-gray-800 transition-colors flex-shrink-0"
                                                    aria-label={t('cart.delete')}
                                                >
                                                    <X size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-lg border border-gray-400 shadow-sm p-6 sticky top-4">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('cart.paymentInfo')}</h3>
                                <div className="space-y-3 mb-4 pb-4 border-b border-gray-200">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">{t('cart.selectedClasses')}</span>
                                        <span className="font-medium text-gray-900">{totalSelectedCount} {t('cart.countUnit')}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">{t('cart.totalProductAmount')}</span>
                                        <span className="font-medium text-gray-900">
                                            {formatCurrencyForLocale(
                                                selectedEntries.reduce((sum, e) => {
                                                    const it = classes[e.id] as ClassItem | undefined;
                                                    return sum + (it?.price ?? 0) * e.quantity;
                                                }, 0),
                                                i18n.language
                                            )}
                                        </span>
                                    </div>
                                    {selectedEntries.some(e => (classes[e.id] as ClassItem)?.discountPrice !== undefined) && (
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600">{t('cart.discountAmount')}</span>
                                            <span className="font-medium text-red-600">
                                                -{formatCurrencyForLocale(
                                                    selectedEntries.reduce((sum, e) => {
                                                        const it = classes[e.id] as ClassItem | undefined;
                                                        if (!it || it.discountPrice == null) return sum;
                                                        return sum + ((it.price ?? 0) - it.discountPrice) * e.quantity;
                                                    }, 0),
                                                    i18n.language
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center justify-between mb-6 pt-2">
                                    <span className="text-base font-bold text-gray-900">{t('cart.finalAmount')}</span>
                                    <span className="text-xl font-semibold text-blue-500">{formatCurrencyForLocale(totalPrice, i18n.language)}</span>
                                </div>
                                <div className="flex flex-col gap-2">
                                        <div className="flex flex-row gap-2">
                                            <Button
                                                variant="primary"
                                                size="md"
                                                className="flex-1"
                                                onClick={handleCheckout}
                                                disabled={selectedItems.size === 0}
                                            >
                                                {selectedItems.size > 0 ? t('cart.onlineOrder') : t('cart.selectProductsFirst')}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="md"
                                                className="flex-1"
                                                onClick={handleQuoteRequest}
                                                disabled={selectedItems.size === 0}
                                            >
                                                {t('cart.quoteInquiry')}
                                            </Button>
                                        </div>
                                    </div>
                            </div>
                        </div>
                    </div>
                )}

            <Modal
                isOpen={quoteModalOpen}
                onClose={() => setQuoteModalOpen(false)}
                title={t('quoteModal.title')}
                size="medium"
                footer={
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setQuoteModalOpen(false)}>{t('quoteModal.cancel')}</Button>
                        <Button variant="primary" onClick={submitQuoteRequest}>{t('quoteModal.submitAgree')}</Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('quoteModal.inquiryProducts')}</h4>
                        <ul className="space-y-1.5 text-sm text-gray-800">
                            {selectedEntries.map((entry) => {
                                const item = classes[entry.id] as ClassItem | undefined;
                                if (!item) return null;
                                const displayItem = getLocalizedClassItem(item, i18n.language);
                                return (
                                    <li key={entry.id}>
                                        {displayItem.title}
                                        <span className="text-gray-500 ml-1">× {entry.quantity}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                    <Input
                        label={t('quoteModal.organization')}
                        value={quoteForm.organizationName}
                        onChange={(e) => setQuoteForm(f => ({ ...f, organizationName: e.target.value }))}
                        placeholder={t('quoteModal.organizationPlaceholder')}
                    />
                    <Input
                        label={t('quoteModal.name')}
                        value={quoteForm.name}
                        onChange={(e) => setQuoteForm(f => ({ ...f, name: e.target.value }))}
                        placeholder={t('quoteModal.namePlaceholder')}
                    />
                    <Input
                        label={t('quoteModal.phone')}
                        type="tel"
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm(f => ({ ...f, phone: e.target.value }))}
                        placeholder={t('quoteModal.phonePlaceholder')}
                    />
                    <Input
                        label={t('quoteModal.email')}
                        type="email"
                        value={quoteForm.email}
                        onChange={(e) => setQuoteForm(f => ({ ...f, email: e.target.value }))}
                        placeholder={t('quoteModal.emailPlaceholder')}
                    />
                    <Input
                        label={t('quoteModal.message')}
                        multiline
                        rows={4}
                        value={quoteForm.message}
                        onChange={(e) => setQuoteForm(f => ({ ...f, message: e.target.value }))}
                        placeholder={t('quoteModal.messagePlaceholder')}
                    />
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                        <h4 className="font-semibold text-gray-900 mb-2">{t('quoteModal.privacyTitle')}</h4>
                        <p className="mb-2">
                            {t('quoteModal.privacyIntro')}
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li>{t('quoteModal.privacyItems')}</li>
                            <li>{t('quoteModal.privacyPurpose')}</li>
                            <li>{t('quoteModal.privacyRetention')}</li>
                        </ul>
                    </div>
                </div>
            </Modal>
            </div>
        </div>
    );
}
